<template>
  <div class="reader-container">
    <header class="reader-header">
      <div class="header-content">
        <div class="header-brand">
          <h1>Enrico Michael Severe</h1>
          <p>Private Researcher</p>
        </div>
        <nav class="header-nav">
          <button v-if="user" @click="switchToAdmin" class="nav-link">Dashboard</button>
          <button v-if="user" @click="handleLogout" class="nav-link">Logout</button>
        </nav>
      </div>
    </header>

    <div v-if="selectedPost" class="post-detail-view">
      <button @click="selectedPost = null" class="back-btn">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
        Back to all posts
      </button>

      <article class="post-detail">
        <div v-if="selectedPost.imageUrl" class="post-hero">
          <img :src="selectedPost.imageUrl" :alt="selectedPost.title" />
        </div>

        <div class="post-header">
          <div class="post-meta-row">
            <span class="post-date">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              {{ formatDate(selectedPost.createdAt) }}
            </span>
            <span v-if="selectedPost.category" class="post-category-badge">
              {{ selectedPost.category }}
            </span>
          </div>
          <h1 class="post-title">{{ selectedPost.title }}</h1>
          <p v-if="selectedPost.excerpt" class="post-excerpt-detail">{{ selectedPost.excerpt }}</p>
          <div v-if="selectedPost.tags && selectedPost.tags.length > 0" class="post-tags">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
              <line x1="7" y1="7" x2="7.01" y2="7"></line>
            </svg>
            <span v-for="(tag, index) in selectedPost.tags" :key="index" class="tag">
              {{ tag }}
            </span>
          </div>
        </div>

        <div class="post-content" v-html="renderMarkdown(selectedPost.content)"></div>
      </article>
    </div>

    <div v-else class="reader-content">
      <div class="reader-hero">
        <div class="reader-hero-eyebrow">Research &amp; Reflections</div>
        <h2>Thoughts on Power,<br>Systems &amp; <em>Change</em></h2>
        <p>Critical analysis of the forces shaping modern society</p>
      </div>

      <div v-if="postsLoading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading posts...</p>
      </div>

      <div v-else-if="posts.length === 0" class="empty-state-reader">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
        </svg>
        <h3>No posts yet</h3>
        <p>Check back soon for new content</p>
      </div>

      <div v-else class="posts-masonry">
        <div
          v-for="post in posts"
          :key="post.id"
          class="post-card-reader"
          @click="viewPost(post)"
        >
          <div v-if="post.imageUrl" class="card-image">
            <img :src="post.imageUrl" :alt="post.title" />
            <div class="card-image-overlay"></div>
          </div>
          <div class="card-body">
            <div class="card-meta">
              <span class="card-date">{{ formatDate(post.createdAt) }}</span>
              <span v-if="post.category" class="card-category">{{ post.category }}</span>
            </div>
            <h3 class="card-title">{{ post.title }}</h3>
            <p class="card-excerpt">{{ post.excerpt || truncate(post.content, 150) }}</p>
            <div class="card-footer">
              <span class="read-more">
                Read more
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db, auth } from '../firebase'

export default {
  name: 'ReaderView',
  data () {
    return {
      user: null,
      posts: [],
      postsLoading: false,
      selectedPost: null,
    }
  },
  mounted () {
    auth.onAuthStateChanged(user => { this.user = user })
    this.loadPosts()
  },
  methods: {
    switchToAdmin () { this.$router.push('/admin') },
    async handleLogout () {
      try { await auth.signOut(); this.user = null } catch (e) { console.error(e) }
    },
    async loadPosts () {
      this.postsLoading = true
      try {
        const snapshot = await db.collection('posts')
          .where('status', '==', 'published')
          .orderBy('createdAt', 'desc')
          .get()
        this.posts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (e) { console.error(e) } finally { this.postsLoading = false }
    },
    viewPost (post) {
      this.selectedPost = post
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    formatDate (timestamp) {
      if (!timestamp) return 'Just now'
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    truncate (text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    },
    renderMarkdown (content) {
      if (!content) return ''
      let html = content
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>')
      return '<p>' + html + '</p>'
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400&family=Inter:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,700;1,400&display=swap');

/* ─── Base ─────────────────────────────────────────────────── */
.reader-container {
  min-height: 100vh;
  background: #0d1117;
  color: #f5f0e8;
}

/* ─── Header ───────────────────────────────────────────────── */
.reader-header {
  background: #0b1628;
  border-bottom: 1px solid rgba(201,168,76,0.15);
  padding: 28px 40px;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-brand h1 {
  font-family: 'Playfair Display', serif;
  font-size: 26px;
  font-weight: 700;
  color: #f5f0e8;
  margin-bottom: 4px;
  letter-spacing: -0.3px;
}

.header-brand p {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a6e2a;
  font-style: normal;
}

.header-nav { display: flex; gap: 12px; }

.nav-link {
  padding: 9px 18px;
  background: rgba(201,168,76,0.08);
  border: 1px solid rgba(201,168,76,0.2);
  color: #c9a84c;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.04em;
}

.nav-link:hover {
  background: rgba(201,168,76,0.15);
  border-color: rgba(201,168,76,0.4);
  color: #e8cc80;
}

/* ─── Reader Hero ──────────────────────────────────────────── */
.reader-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 64px 40px;
}

.reader-hero {
  text-align: center;
  margin-bottom: 64px;
  padding-bottom: 64px;
  border-bottom: 1px solid rgba(201,168,76,0.1);
  position: relative;
}

.reader-hero-eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #c9a84c;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.reader-hero-eyebrow::before,
.reader-hero-eyebrow::after {
  content: '';
  display: block;
  width: 32px;
  height: 1px;
  background: #c9a84c;
}

.reader-hero h2 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(40px, 5vw, 64px);
  font-weight: 700;
  color: #f5f0e8;
  margin-bottom: 20px;
  letter-spacing: -1px;
  line-height: 1.1;
}

.reader-hero h2 em {
  color: #c9a84c;
  font-style: italic;
}

.reader-hero p {
  font-family: 'Inter', sans-serif;
  font-size: 17px;
  color: #718096;
  font-weight: 300;
  font-style: normal;
}

/* ─── Loading / Empty ──────────────────────────────────────── */
.loading-state, .empty-state-reader {
  text-align: center;
  padding: 100px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  margin: 0 auto 20px;
  border: 3px solid rgba(201,168,76,0.15);
  border-top-color: #c9a84c;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.loading-state p, .empty-state-reader p {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: #718096;
}

.empty-state-reader svg { margin: 0 auto 28px; color: #374151; display: block; }

.empty-state-reader h3 {
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  color: #c8c0b0;
  margin-bottom: 12px;
}

/* ─── Post Grid ────────────────────────────────────────────── */
.posts-masonry {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 2px;
}

.post-card-reader {
  background: #132038;
  border: 1px solid rgba(201,168,76,0.08);
  overflow: hidden;
  transition: all 0.35s cubic-bezier(0.4,0,0.2,1);
  cursor: pointer;
}

.post-card-reader:hover {
  border-color: rgba(201,168,76,0.3);
  transform: translateY(-4px);
  box-shadow: 0 16px 40px rgba(0,0,0,0.5);
}

.card-image {
  width: 100%;
  height: 240px;
  overflow: hidden;
  background: #0d1827;
  position: relative;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.post-card-reader:hover .card-image img { transform: scale(1.06); }

.card-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(19,32,56,0.7) 0%, transparent 60%);
}

.card-body { padding: 28px; }

.card-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.card-date {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #4b5563;
}

.card-category {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #c9a84c;
  background: rgba(201,168,76,0.1);
  border: 1px solid rgba(201,168,76,0.2);
  padding: 3px 10px;
}

.card-title {
  font-family: 'Playfair Display', serif;
  font-size: 24px;
  font-weight: 700;
  color: #f5f0e8;
  line-height: 1.3;
  margin-bottom: 14px;
  transition: color 0.2s;
}

.post-card-reader:hover .card-title { color: #e8cc80; }

.card-excerpt {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.7;
  color: #718096;
  margin-bottom: 20px;
  font-weight: 300;
}

.card-footer {
  display: flex;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid rgba(201,168,76,0.08);
}

.read-more {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #c9a84c;
  transition: gap 0.2s;
}

.post-card-reader:hover .read-more { gap: 10px; }

/* ─── Post Detail ──────────────────────────────────────────── */
.post-detail-view {
  max-width: 860px;
  margin: 0 auto;
  padding: 48px 24px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: transparent;
  border: 1px solid rgba(201,168,76,0.2);
  color: #c9a84c;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 36px;
}

.back-btn:hover {
  background: rgba(201,168,76,0.08);
  border-color: rgba(201,168,76,0.4);
  color: #e8cc80;
}

.post-detail {
  background: #0f1a2e;
  border: 1px solid rgba(201,168,76,0.12);
  overflow: hidden;
}

.post-hero {
  width: 100%;
  height: 460px;
  overflow: hidden;
  background: #0d1827;
}

.post-hero img { width: 100%; height: 100%; object-fit: cover; }

.post-header {
  padding: 48px;
  border-bottom: 1px solid rgba(201,168,76,0.1);
}

.post-meta-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.post-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #4b5563;
}

.post-category-badge {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #0b1628;
  background: #c9a84c;
  padding: 5px 14px;
}

.post-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(32px, 4vw, 52px);
  font-weight: 700;
  color: #f5f0e8;
  line-height: 1.15;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
}

.post-excerpt-detail {
  font-family: 'Playfair Display', serif;
  font-size: 20px;
  line-height: 1.6;
  color: #8a6e2a;
  font-style: italic;
  margin-bottom: 24px;
}

.post-tags {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.post-tags svg { color: #4b5563; }

.tag {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #c8c0b0;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(201,168,76,0.12);
  padding: 5px 12px;
}

/* ─── Post Content ─────────────────────────────────────────── */
.post-content {
  padding: 48px;
  font-family: 'Crimson Pro', serif;
  font-size: 20px;
  line-height: 1.85;
  color: #c8c0b0;
}

.post-content >>> h1 {
  font-family: 'Playfair Display', serif;
  font-size: 42px;
  font-weight: 700;
  color: #f5f0e8;
  margin: 56px 0 24px;
  line-height: 1.2;
  letter-spacing: -0.5px;
}

.post-content >>> h2 {
  font-family: 'Playfair Display', serif;
  font-size: 32px;
  font-weight: 700;
  color: #f5f0e8;
  margin: 48px 0 20px;
  line-height: 1.25;
}

.post-content >>> h3 {
  font-family: 'Playfair Display', serif;
  font-size: 24px;
  font-weight: 700;
  color: #e8cc80;
  margin: 36px 0 16px;
  line-height: 1.35;
}

.post-content >>> p { margin-bottom: 24px; }

.post-content >>> strong {
  font-weight: 700;
  color: #f5f0e8;
}

.post-content >>> em { font-style: italic; color: #e8cc80; }

.post-content >>> blockquote {
  margin: 36px 0;
  padding: 24px 32px;
  border-left: 3px solid #c9a84c;
  background: rgba(201,168,76,0.05);
  font-style: italic;
  color: #c8c0b0;
  font-size: 21px;
}

.post-content >>> ul,
.post-content >>> ol {
  margin: 24px 0;
  padding-left: 28px;
}

.post-content >>> li { margin-bottom: 10px; }

.post-content >>> code {
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.1);
  padding: 3px 8px;
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: #c9a84c;
}

.post-content >>> pre {
  background: #060e1a;
  border: 1px solid rgba(201,168,76,0.1);
  padding: 28px;
  overflow-x: auto;
  margin: 32px 0;
}

.post-content >>> pre code {
  background: transparent;
  border: none;
  color: #c8c0b0;
  padding: 0;
}

/* ─── Responsive ────────────────────────────────────────────── */
@media (max-width: 768px) {
  .reader-content { padding: 48px 20px; }
  .posts-masonry { grid-template-columns: 1fr; }
  .post-detail-view { padding: 32px 16px; }
  .post-hero { height: 260px; }
  .post-header, .post-content { padding: 28px 24px; }
  .reader-header { padding: 20px 24px; }
}
</style>